import { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TripFiltersContext } from '../../context/trip-filters.context';
import { Grid, Button, Container, useTheme, Box } from '@mui/material';
import SelectField from '../select-field/select-field.component';
import DateField from '../date-field/date-field.component';
import CountField from '../count-field/count-field.component';
import LocalIcon from '../../assets/icons/localizacao.svg';
import { ReactComponent as CalendarInIcon } from '../../assets/icons/calendar entrada.svg';
import { ReactComponent as CalendarOutIcon } from '../../assets/icons/calendar saida.svg';
import { ReactComponent as SearchIcon } from '../../assets/icons/search.svg';
import dayjs from 'dayjs';

const TripSearcher = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    destination,
    setDestination,
    boardingDate,
    setBoardingDate,
    arrivalDate,
    setArrivalDate,
    youngPassengers,
    setYoungPassengers,
    oldPassengers,
    setOldPassengers,
  } = useContext(TripFiltersContext);

  const defaultFormFields = {
    destination: destination,
    boardingDate: boardingDate ? dayjs(boardingDate) : null,
    arrivalDate: arrivalDate ? dayjs(arrivalDate) : null,
    youngPassengers: youngPassengers,
    oldPassengers: oldPassengers,
  };

  const [formFields, setFormFields] = useState(defaultFormFields);

  useEffect(() => {
    const params = {
      destination: searchParams.get('destination'),
      boardingDate: searchParams.get('boardingDate')
        ? dayjs(searchParams.get('boardingDate'))
        : null,
      arrivalDate: searchParams.get('arrivalDate')
        ? dayjs(searchParams.get('arrivalDate'))
        : null,
      youngPassengers: Number(searchParams.get('youngPassengers')) || 1,
      oldPassengers: Number(searchParams.get('oldPassengers')),
    };

    setFormFields(params);
    setDestination(params.destination);
    setBoardingDate(params.boardingDate);
    setArrivalDate(params.arrivalDate);
    setYoungPassengers(params.youngPassengers);
    setOldPassengers(params.oldPassengers);
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === 'boardingDate' || name === 'arrivalDate') {
      setFormFields({
        ...formFields,
        [name]: value ? dayjs(value) : null,
      });
    } else {
      setFormFields({
        ...formFields,
        [name]: value,
      });
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setDestination(formFields.destination);
    setBoardingDate(formFields.boardingDate);
    setArrivalDate(formFields.arrivalDate);
    setYoungPassengers(formFields.youngPassengers);
    setOldPassengers(formFields.oldPassengers);
    navigate('/search-results');
  };

  const destinations = [
    'África',
    'América do Norte',
    'América Central',
    'América do Sul',
    'Ásia',
    'Europa',
    'Oceania',
  ];

  const theme = useTheme();

  return (
    <Container
      disableGutters
      sx={{
        maxWidth: '1018px',
        backgroundColor: `${theme.palette.primary.main}`,
        borderRadius: { xs: '20px', md: '48px' },
        padding: {
          xs: `${theme.spacingValues.large} ${theme.spacingValues.medium}`,
          md: `${theme.spacingValues.large}`,
        },
        marginX: {
          xs: '0.5rem',
          md: 'auto',
        },
        paddingBottom: `calc(${theme.spacingValues.large} * 1.5) !important`,
        width: {
          xs: 'calc(100% - 1rem)',
          md: '100%',
        },
      }}
    >
      <form onSubmit={handleSubmit}>
        <Grid
          container
          spacing={{
            xs: theme.spacingValues.medium,
            md: theme.spacingValues.mediumLarge,
          }}
          sx={{
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'stretch',
          }}
        >
          {/* DESTINO */}
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: {
                xs: theme.spacingValues.medium,
                md: theme.spacingValues.mediumLarge,
              },
            }}
          >
            <SelectField
              label="Selecione o seu destino"
              type="text"
              name="destination"
              icon={LocalIcon}
              required
              onChange={handleChange}
              value={destination}
              options={destinations}
            />
          </Grid>

          {/* EMBARQUE 2 */}
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: {
                xs: theme.spacingValues.medium,
                md: theme.spacingValues.mediumLarge,
              },
            }}
          >
            <DateField
              label="Embarque no Brasil"
              name="boardingDate"
              icon={CalendarInIcon}
              required
              onChange={handleChange}
              value={boardingDate}
            />
          </Grid>

          {/* DESEMBARQUE 3 */}
          <Grid
            size={{ xs: 12, md: 4 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: {
                xs: theme.spacingValues.medium,
                md: theme.spacingValues.mediumLarge,
              },
              justifyContent: 'space-between',
            }}
          >
            <DateField
              label="Desembarque no Brasil"
              name="arrivalDate"
              icon={CalendarOutIcon}
              required
              onChange={handleChange}
              value={arrivalDate}
              minDate={formFields.boardingDate}
            />
          </Grid>

          {/* YOUNG PASSENGERS */}

          <Grid size={{ xs: 12, md: 4 }}>
            <CountField
              label="Passageiros até 60 anos"
              name="youngPassengers"
              onChange={handleChange}
              value={youngPassengers}
              minValue={formFields.oldPassengers !== 0 ? 0 : 1}
            />
          </Grid>

          {/* OLD PASSENGERS */}

          <Grid size={{ xs: 12, md: 4 }}>
            <CountField
              label="De 60 até 85 anos"
              name="oldPassengers"
              onChange={handleChange}
              value={oldPassengers}
              minValue={formFields.youngPassengers !== 0 ? 0 : 1}
            />
          </Grid>

          {/* BUTTON PASSENGERS */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'stretch' }}>
              <Button
                type="submit"
                className="search-button"
                variant="contained"
                size="small"
                fullWidth
                sx={{
                  color: 'primary.contrastText',
                  backgroundColor: 'primary.dark',
                  '&:hover': {
                    backgroundColor: 'primary.main',
                  },
                  borderRadius: '8px',
                  padding: '8px 16px',
                  textTransform: 'none',
                  justifyContent: 'space-between',
                  flex: 1,
                  minHeight: '45px',
                  fontWeight: 700,
                }}
                endIcon={
                  <SearchIcon
                    style={{ color: `${theme.palette.primary.contrastText}` }}
                  />
                }
              >
                Pesquisar
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Container>
  );
};

export default TripSearcher;
